import React, { useEffect, useState } from "react";
import logo from "./logo.svg";
import klaudiaPhoto from "./assets/Klaudia.png";
import "./App.css";

type NewsItem = {
  title?: string;
  body?: string;
};

type AktualityJson = string | NewsItem | NewsItem[];

function normalizeNewlines(s: string) {
  // podporí aj keď v JSON skončí doslova "\n"
  return (s ?? "").replace(/\\n/g, "\n").trim();
}

function parseAktuality(raw: AktualityJson | undefined | null): NewsItem[] {
  if (!raw) return [];

  // 1) Array: [{title, body}, ...]
  if (Array.isArray(raw)) {
    return raw
      .map((it) => ({
        title: (it?.title ?? "").trim(),
        body: normalizeNewlines(it?.body ?? ""),
      }))
      .filter((it) => it.title || it.body);
  }

  // 2) String: "TITLE\nBODY..."
  if (typeof raw === "string") {
    const normalized = normalizeNewlines(raw);
    if (!normalized) return [];

    const lines = normalized.split("\n");
    const first = (lines[0] ?? "").trim();
    const rest = lines.slice(1).join("\n").trim();

    // ak je to len 1 riadok, berieme to ako body (bez title)
    if (!rest) return [{ title: "", body: normalized }];

    return [{ title: first, body: rest }];
  }

  // 3) Object: {title, body}
  return [
    {
      title: (raw.title ?? "").trim(),
      body: normalizeNewlines(raw.body ?? ""),
    },
  ].filter((it) => it.title || it.body);
}

function IconWrap({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "rgba(255,255,255,0.9)",
      }}
    >
      {children}
    </span>
  );
}

function MapPinIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 21s-7-7.05-7-11.5A7 7 0 0 1 19 9.5C19 13.95 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 21v-4h6v4M9 8h1M9 12h1M14 8h1M14 12h1" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function App() {
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetch(`/content.json?v=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((data) => {
        const list = parseAktuality(data?.aktuality);
        setNewsList(list);
      })
      .catch(() => setNewsList([]));
  }, []);

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false); // Zatvorí menu po kliknutí
  };

  const openingHours = [
    { day: "Pondelok", time: "7:30 – 12:00" },
    { day: "Utorok", time: "12:00 – 16:00" },
    { day: "Streda", time: "7:30 – 12:00" },
    { day: "Štvrtok", time: "7:30 – 12:00" },
    { day: "Piatok", time: "7:30 – 12:00" },
  ];
  const weekdayNames = [
    "Nedeľa",
    "Pondelok",
    "Utorok",
    "Streda",
    "Štvrtok",
    "Piatok",
    "Sobota",
  ];
  const todayName = weekdayNames[new Date().getDay()];

  const contactItems = [
    {
      icon: <MapPinIcon />,
      label: "Adresa",
      content: (
        <a
          className="kontakt-link"
          target="_blank"
          rel="noreferrer"
          href="https://maps.app.goo.gl/GqUPMLj9Jzom14S88"
        >
          Československej armády 1175, 040 01 Košice, Slovensko
        </a>
      ),
    },
    {
      icon: <BuildingIcon />,
      label: "Prevádzkovateľ",
      content: (
        <>
          <div>STAJAMED s. r. o.</div>
          <div>Čsl. armády 18, 040 01 Košice</div>
          <div style={{ opacity: 0.7 }}>
            IČO: 46691049 · DIČ: 2023588831
          </div>
        </>
      ),
    },
    {
      icon: <FacebookIcon />,
      label: "Sledujte nás",
      content: (
        <a
          className="kontakt-link"
          style={{ borderBottom: "none" }}
          href="https://www.facebook.com/profile.php?id=61580848597734"
          target="_blank"
          rel="noopener noreferrer"
        >
          Facebook
        </a>
      ),
    },
    {
      icon: <PhoneIcon />,
      label: "Telefón",
      content: (
        <a className="kontakt-link" href="tel:+421917072509">
          +421 917 072 509
        </a>
      ),
    },
    {
      icon: <MailIcon />,
      label: "Email",
      content: (
        <a className="kontakt-link" href="mailto:jahodkamed@gmail.com">
          jahodkamed@gmail.com
        </a>
      ),
    },
  ];

  return (
    <div className="App" style={{ position: "relative" }}>
      {/* Mobile menu overlay */}
      <div
        className={`mobile-menu-overlay ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
        style={{
          display: menuOpen ? "block" : "none",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 999,
        }}
      />

      {/* Mobile menu */}
      <div
        className={`mobile-menu ${menuOpen ? "open" : ""}`}
        style={{
          display: menuOpen ? "flex" : "none",
          position: "fixed",
          top: 0,
          right: 0,
          width: "280px",
          height: "100vh",
          backgroundColor: "white",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          zIndex: 1000,
          flexDirection: "column",
          padding: "80px 20px 20px 20px",
          transition: "transform 0.3s ease",
        }}
      >
        <div
          onClick={() =>
            window.open("https://ordinex.sk/ambulancia-jahodka", "_blank")
          }
          className="mobile-nav-item"
          style={{
            padding: "15px 10px",
            fontSize: "18px",
            fontWeight: 600,
            color: "rgb(72, 56, 76)",
            cursor: "pointer",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          Rezervácia
        </div>
        <div
          onClick={() => scrollToSection("aktuality")}
          className="mobile-nav-item"
          style={{
            padding: "15px 10px",
            fontSize: "18px",
            fontWeight: 600,
            color: "rgb(72, 56, 76)",
            cursor: "pointer",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          Aktuality
        </div>
        <div
          onClick={() => scrollToSection("oznamy")}
          className="mobile-nav-item"
          style={{
            padding: "15px 10px",
            fontSize: "18px",
            fontWeight: 600,
            color: "rgb(72, 56, 76)",
            cursor: "pointer",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          Oznamy
        </div>
        <div
          onClick={() => scrollToSection("onas")}
          className="mobile-nav-item"
          style={{
            padding: "15px 10px",
            fontSize: "18px",
            fontWeight: 600,
            color: "rgb(72, 56, 76)",
            cursor: "pointer",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          O nás
        </div>
        <div
          onClick={() => scrollToSection("kontakt")}
          className="mobile-nav-item"
          style={{
            padding: "15px 10px",
            fontSize: "18px",
            fontWeight: 600,
            color: "rgb(72, 56, 76)",
            cursor: "pointer",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          Kontakt
        </div>
      </div>

      {/* ✅ HEADER – jemnejšie pozadie, aby ladilo so striedaním */}
      <header
        style={{ backgroundColor: "#fdf3f3", position: "relative", zIndex: 10 }}
      >
        {/* Dekoratívne jahôdky v headeri */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            opacity: 0.18,
            fontSize: "40px",
            zIndex: 0,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "5%",
              left: "5%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "30%",
              left: "25%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "40%",
              left: "15%",
              transform: "rotate(25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "30%",
              right: "8%",
              transform: "rotate(-20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "70%",
              right: "18%",
              transform: "rotate(15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "20%",
              right: "30%",
              transform: "rotate(110deg)",
            }}
          >
            🍓
          </div>
        </div>
        <div
          className="header-container"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {/* Logo + Hamburger wrapper */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              padding: "20px",
              width: "350px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <img src={logo} alt="Logo" style={{ height: "60px" }} />
              <span style={{ fontSize: "19px", fontWeight: "bold" }}>
                Detská ambulancia
                <br />
                Jahôdka
              </span>
            </div>

            {/* Hamburger menu button - bude vedľa loga na mobile */}
            <div
              className="hamburger"
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                display: "none",
                flexDirection: "column",
                cursor: "pointer",
                padding: "10px",
                zIndex: 1001,
              }}
            >
              <span
                style={{
                  width: "25px",
                  height: "3px",
                  backgroundColor: "rgb(72, 56, 76)",
                  margin: "3px 0",
                  borderRadius: "2px",
                }}
              ></span>
              <span
                style={{
                  width: "25px",
                  height: "3px",
                  backgroundColor: "rgb(72, 56, 76)",
                  margin: "3px 0",
                  borderRadius: "2px",
                }}
              ></span>
              <span
                style={{
                  width: "25px",
                  height: "3px",
                  backgroundColor: "rgb(72, 56, 76)",
                  margin: "3px 0",
                  borderRadius: "2px",
                }}
              ></span>
            </div>
          </div>

          {/* Desktop navigation */}
          <div
            className="desktop-nav"
            style={{
              display: "flex",
              gap: "10px",
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            <div
              onClick={() =>
                window.open("https://ordinex.sk/ambulancia-jahodka", "_blank")
              }
              className="nav-item"
            >
              Rezervácia
            </div>
            <div
              onClick={() => scrollToSection("aktuality")}
              className="nav-item"
            >
              Aktuality
            </div>

            {/* ✅ NOVÉ: Oznamy */}
            <div onClick={() => scrollToSection("oznamy")} className="nav-item">
              Oznamy
            </div>

            <div onClick={() => scrollToSection("onas")} className="nav-item">
              O nás
            </div>
            <div
              onClick={() => scrollToSection("kontakt")}
              className="nav-item"
            >
              Kontakt
            </div>
          </div>
        </div>
      </header>

      <style>{`
        @media (max-width: 768px) {
          .hamburger {
            display: flex !important;
          }
          .desktop-nav {
            display: none !important;
          }
          .header-container > div:first-child {
            width: 100% !important;
            padding: 10px 20px !important;
          }
          .header-container > div:first-child > div:first-child img {
            height: 50px !important;
          }
          .hero > div {
            flex-direction: column !important;
          }
          h1 {
            font-size: 36px !important;
          }
          .oznamy-grid {
            grid-template-columns: 1fr !important;
          }
          .preco-grid {
            grid-template-columns: 1fr !important;
          }
          .onas-content {
            flex-direction: column !important;
            gap: 20px !important;
          }
          .onas-text {
            flex: 1 1 auto !important;
          }
          .onas-text a {
            display: block !important;
            text-align: center !important;
          }
          .onas-photo {
            flex: 1 1 auto !important;
          }
          .onas-photo img {
            width: 100% !important;
            max-width: 280px !important;
            height: auto !important;
          }
          .kontakt-grid {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .kontakt-item {
            max-width: 100% !important;
          }
        }
      `}</style>

      {/* ✅ HERO – kompaktnejšia výška */}
      <div
        className="hero"
        style={{
          backgroundColor: "#ffffff",
          padding: "24px 20px", // ⬅️ bolo 40px
          position: "relative",
          zIndex: 10,
          maxHeight: "500px", //
        }}
      >
        <h1
          className="hero-title"
          style={{
            fontSize: "56px", // ⬅️ bolo 56px
            marginTop: 0,
            marginBottom: "12px",
            textAlign: "center",
            lineHeight: "1.15",
          }}
        >
          Starostlivosť, ktorú si vaše dieťa zaslúži
        </h1>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "14px",
            marginTop: "16px", // ⬅️ menej priestoru
            flexWrap: "wrap",
          }}
        >
          <button
            style={{
              padding: "10px 20px",
              fontSize: "15px",
              fontWeight: "800",
              cursor: "pointer",
              backgroundColor: "rgb(72, 56, 76)",
              color: "white",
              border: "none",
              borderRadius: "6px",
            }}
            onClick={() =>
              window.open("https://ordinex.sk/ambulancia-jahodka", "_blank")
            }
          >
            Objednať sa online
          </button>

          <button
            style={{
              padding: "10px 20px",
              fontSize: "15px",
              fontWeight: "800",
              cursor: "pointer",
              backgroundColor: "rgb(72, 56, 76)",
              color: "white",
              border: "none",
              borderRadius: "6px",
            }}
            onClick={() => scrollToSection("kontakt")}
          >
            Kontakt
          </button>
        </div>
      </div>

      {/* ✅ AKTUALITY – krémová béžová s viacerými jahôdkami */}
      <div
        id="aktuality"
        style={{
          backgroundColor: "#F5EFE7",
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* Extra jahôdky len pre sekciu Aktuality */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            opacity: 0.2,
            fontSize: "45px",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "5%",
              left: "5%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "8%",
              right: "20%",
              transform: "rotate(25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "10%",
              left: "32%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "10%",
              right: "28%",
              transform: "rotate(-20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "5%",
              left: "45%",
              transform: "rotate(15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "1%",
              right: "52%",
              transform: "rotate(-10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "3%",
              left: "62%",
              transform: "rotate(70deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "6%",
              right: "83%",
              transform: "rotate(-25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "6%",
              left: "93%",
              transform: "rotate(80deg)",
            }}
          >
            🍓
          </div>

          <div
            style={{
              position: "absolute",
              top: "22%",
              left: "6%",
              transform: "rotate(20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "28%",
              right: "12%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "32%",
              left: "18%",
              transform: "rotate(25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "30%",
              right: "25%",
              transform: "rotate(-20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "27%",
              left: "38%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "35%",
              right: "45%",
              transform: "rotate(-30deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "29%",
              left: "58%",
              transform: "rotate(15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "33%",
              right: "65%",
              transform: "rotate(-10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "26%",
              left: "88%",
              transform: "rotate(25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "31%",
              right: "95%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>

          <div
            style={{
              position: "absolute",
              top: "48%",
              left: "6%",
              transform: "rotate(-20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "52%",
              right: "2%",
              transform: "rotate(15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "10%",
              transform: "rotate(-10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "55%",
              right: "30%",
              transform: "rotate(20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "47%",
              left: "42%",
              transform: "rotate(-25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "53%",
              right: "50%",
              transform: "rotate(30deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "49%",
              left: "62%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "54%",
              right: "72%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "51%",
              left: "85%",
              transform: "rotate(25deg)",
            }}
          >
            🍓
          </div>

          <div
            style={{
              position: "absolute",
              top: "68%",
              left: "9%",
              transform: "rotate(15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "72%",
              right: "8%",
              transform: "rotate(-20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "70%",
              left: "25%",
              transform: "rotate(25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "75%",
              right: "35%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "80%",
              right: "3%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "35%",
              right: "3%",
              transform: "rotate(-15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "22%",
              right: "2%",
              transform: "rotate(-45deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "69%",
              left: "48%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "61%",
              left: "6%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "55%",
              left: "2%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "73%",
              left: "3%",
              transform: "rotate(10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "73%",
              right: "58%",
              transform: "rotate(-25deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "71%",
              left: "92%",
              transform: "rotate(20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "82%",
              right: "97%",
              transform: "rotate(-10deg)",
            }}
          >
            🍓
          </div>

          <div
            style={{
              position: "absolute",
              top: "88%",
              left: "12%",
              transform: "rotate(-30deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "92%",
              right: "3%",
              transform: "rotate(15deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "90%",
              left: "7%",
              transform: "rotate(-10deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "94%",
              left: "26%",
              transform: "rotate(150deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "94%",
              right: "45%",
              transform: "rotate(20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "94%",
              right: "30%",
              transform: "rotate(100deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "95%",
              left: "2%",
              transform: "rotate(-20deg)",
            }}
          >
            🍓
          </div>
          <div
            style={{
              position: "absolute",
              top: "97%",
              right: "1%",
              transform: "rotate(40deg)",
            }}
          >
            🍓
          </div>
        </div>

        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "20px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <h1 style={{ fontSize: "46px" }}>Aktuality</h1>

          {/* ✅ Render viac oznamov */}
          {newsList.map((news, idx) => (
            <div
              key={idx}
              style={{
                background: "white",
                borderLeft: "6px solid rgb(72, 56, 76)",
                padding: "16px",
                borderRadius: "10px",
                marginBottom: "20px",
                lineHeight: "28px",
                boxShadow: "0 8px 22px rgba(0,0,0,0.04)",
              }}
            >
              {news.title && (
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    marginBottom: "8px",
                  }}
                >
                  {news.title}
                </div>
              )}

              {news.body && (
                <div style={{ whiteSpace: "pre-line" }}>{news.body}</div>
              )}
            </div>
          ))}

          {/* Karty vedľa seba */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
              marginTop: "40px",
            }}
            className="aktuality-cards-grid"
          >
            {/* Karta 1 - Noví pacienti */}
            <div
              style={{
                backgroundColor: "white",
                padding: "28px",
                borderRadius: "16px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                border: "1px solid rgba(72,56,76,0.1)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              className="info-card"
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "18px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "rgba(72,56,76,0.1)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                  }}
                >
                  👋
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    color: "rgb(72, 56, 76)",
                  }}
                >
                  Prijímame nových pacientov!
                </h3>
              </div>

              <p
                style={{
                  lineHeight: "28px",
                  marginBottom: "16px",
                  fontSize: "15px",
                }}
              >
                V prípade záujmu o registráciu nám napíšte email alebo nás
                navštívte počas ordinačných hodín v OC Tip-Top v Košiciach
                (1.poschodie, ostro doprava, bývalá ambulancia MUDr. Stašovej).
                Zdravotnú kartu si vyžiadame my a Vy sa už nemusíte starať o nič
                iné.
              </p>

              <div
                style={{
                  backgroundColor: "rgba(72,56,76,0.06)",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  marginBottom: "14px",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    opacity: 0.7,
                    marginBottom: "4px",
                  }}
                >
                  Email
                </div>
                <a
                  style={{
                    color: "rgb(72, 56, 76)",
                    fontWeight: 700,
                    textDecoration: "none",
                    fontSize: "16px",
                  }}
                  href="mailto:jahodkamed@gmail.com"
                >
                  jahodkamed@gmail.com
                </a>
              </div>

              <p
                style={{
                  lineHeight: "26px",
                  fontStyle: "italic",
                  opacity: 0.8,
                  marginBottom: 0,
                  fontSize: "14px",
                }}
              >
                Ďakujeme za Vašu dôveru a tešíme sa na Vašu náštevu!
              </p>
            </div>

            {/* Karta 2 - Online objednávanie */}
            <div
              style={{
                backgroundColor: "white",
                padding: "28px",
                borderRadius: "16px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                border: "1px solid rgba(72,56,76,0.1)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              className="info-card"
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "18px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "rgba(72,56,76,0.1)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                  }}
                >
                  📱
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    color: "rgb(72, 56, 76)",
                  }}
                >
                  Objednávanie online
                </h3>
              </div>

              <p
                style={{
                  lineHeight: "28px",
                  marginBottom: "20px",
                  fontSize: "15px",
                }}
              >
                Naša ambulancia používa systém{" "}
                <a
                  href="https://ordinex.sk"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgb(72, 56, 76)",
                    textDecoration: "underline",
                    fontWeight: 600,
                  }}
                >
                  Ordinex
                </a>
                , ktorý umožňuje rýchle a pohodlné online objednávanie pre
                pacientov bezplatne, bez telefonovania. Stačí si vybrať termín a
                prísť na vyšetrenie v presne určenom čase.
              </p>

              <button
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  fontSize: "16px",
                  fontWeight: 700,
                  cursor: "pointer",
                  backgroundColor: "rgb(72, 56, 76)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  transition: "transform 0.2s ease, background-color 0.2s ease",
                }}
                onClick={() =>
                  window.open("https://ordinex.sk/ambulancia-jahodka", "_blank")
                }
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.backgroundColor = "rgb(85, 66, 90)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.backgroundColor = "rgb(72, 56, 76)";
                }}
              >
                Objednať sa online →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ NOVÁ SEKCIa: OZnamy – iný dizajn (split layout, bez kariet) */}
      <div
        id="oznamy"
        style={{
          background:
            "linear-gradient(180deg,rgb(250, 226, 226) 0%, #ffffff 100%)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "40px 20px",
          }}
        >
          <div style={{ marginBottom: "22px" }}>
            <h1 style={{ fontSize: "46px", margin: 0 }}>
              Oznamy pre pacientov
            </h1>
            <div
              style={{ marginTop: "10px", opacity: 0.75, lineHeight: "28px" }}
            >
              Dôležité informácie k preventívnym prehliadkam a návšteve
              ambulancie.
            </div>
          </div>

          {/* ✅ ZMENA LAYOUTU: vždy 1 stĺpec (pod sebou) */}
          <div
            className="oznamy-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "22px",
              alignItems: "start",
            }}
          >
            {/* TOP – výrazný notice - VÝRAZNEJŠIA ČERVENÁ */}
            <div
              style={{
                borderRadius: "18px",
                padding: "18px 18px",
                backgroundColor: "rgba(230,57,70,0.12)",
                border: "2px solid rgba(230,57,70,0.30)",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                <div
                  style={{
                    fontSize: "18px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    backgroundColor: "rgba(230,57,70,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  😷
                </div>
                <div style={{ fontWeight: 900, fontSize: "18px" }}>
                  Respiračné príznaky
                </div>
              </div>

              <div style={{ marginTop: "10px", lineHeight: "28px" }}>
                V prípade príznakov respiračnej infekcie u vás alebo vášho
                dieťaťa prosíme o nosenie rúška alebo respirátora v čakárni aj v
                ambulancii.
              </div>

              {/* malý footer */}
              <div
                style={{
                  marginTop: "14px",
                  paddingTop: "12px",
                  borderTop: "1px solid rgba(230,57,70,0.20)",
                  opacity: 0.85,
                  fontSize: "14px",
                  lineHeight: "22px",
                }}
              >
                Ďakujeme, že chránite seba aj ostatných pacientov.
              </div>
            </div>
          </div>

          {/* BOTTOM – veľký info panel - GRADIENT */}
          <div
            style={{
              padding: "22px 22px",
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, rgb(72, 56, 76) 0%, rgb(95, 75, 100) 100%)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                }}
              >
                🧠
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: "20px",
                    lineHeight: "28px",
                    color: "white",
                  }}
                >
                  S-PMV vývinové dotazníky pred poradňou
                </div>
                <div
                  style={{ marginTop: "8px", opacity: 0.85, color: "white" }}
                >
                  Prosíme rodičov o vyplnenie dotazníka pred poradňou.
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: "16px",
                lineHeight: "30px",
                color: "white",
              }}
            >
              <p style={{ marginTop: 0 }}>
                V našej ambulancii zavádzame skríningovú metódu –{" "}
                <b>S-PMV vývinové dotazníky</b>, ktoré sú určené k vyšetreniam
                psychomotorického vývinu pri 2.–11. preventívnej prehliadke u
                vašich detí (od 2. vyšetrenia po narodení po preventívnu
                prehliadku v 2-3 rokoch).
              </p>

              <p>
                Prosíme rodičov aby pred{" "}
                <b>samotnou poradňou</b> vyplnili dotazník
                elektronicky na webe:
              </p>

              {/* link ako "chip" */}
              <div style={{ marginTop: "8px", marginBottom: "10px" }}>
                <a
                  href="https://www.zdraviedietata.sk/vyvinove-dotazniky/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 12px",
                    borderRadius: "999px",
                    border: "1px solid rgba(255,255,255,0.3)",
                    backgroundColor: "rgba(255,255,255,0.15)",
                    color: "white",
                    textDecoration: "none",
                    fontWeight: 800,
                    wordBreak: "break-word",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>🔗</span>
                  zdraviedietata.sk/vyvinove-dotazniky
                </a>
              </div>

              <p style={{ marginBottom: 0 }}>
                Po vyplnení bude dotazník odoslaný na náš email a pri
                preventívnej prehliadke si prejdeme spoločne výsledky.
              </p>
            </div>
          </div>

          {/* mobil fallback: keď je úzky display, nech sa to pekne skladá */}
          <div
            style={{
              marginTop: "14px",
              opacity: 0.0,
              height: 0,
              overflow: "hidden",
            }}
          >
            {/* placeholder, nič */}
          </div>
        </div>
      </div>

      {/* ✅ O NÁS – tmavá sekcia (striedanie) */}
      <div
        id="onas"
        style={{
          backgroundColor: "rgb(72, 56, 76)",
          padding: "40px 20px",
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          className="onas-content"
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            color: "white",
            display: "flex",
            gap: "40px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* LEFT – TEXT */}
          <div className="onas-text" style={{ flex: "1 1 500px" }}>
            <h1 style={{ fontSize: "46px", marginTop: 0 }}>
              Váš pediater v Košiciach
            </h1>

            <p style={{ lineHeight: "30px" }}>
              V našej detskej ambulancii kladieme dôraz na láskavý prístup,
              odbornú starostlivosť a individuálny prístup ku každému dieťaťu aj
              mladému dospelému. Poskytujeme komplexnú starostlivosť od
              narodenia až do 25 rokov – sme tu pre bábätká, predškolákov,
              školákov aj dorast. Sledujeme zdravý vývoj, očkujeme, liečime a
              pomáhame rodičom aj pacientom pri každodenných aj špecifických
              otázkach. Našim cieľom je, aby sa pacienti cítili v bezpečí,
              prijatí a v dobrej nálade. Veríme, že zdravé dieťa je šťastné
              dieťa.
            </p>

            <a
              href="cennik-ambulancia.pdf"
              target="_blank"
              style={{
                color: "rgb(72, 56, 76)",
                padding: "10px 20px",
                backgroundColor: "#fff",
                textDecoration: "none",
                marginTop: 20,
                display: "inline-block",
                borderRadius: "6px",
              }}
            >
              Náš cenník
            </a>
          </div>

          {/* RIGHT – PHOTO */}
          <div
            className="onas-photo"
            style={{ flex: "0 0 320px", textAlign: "center" }}
          >
            <img
              src={klaudiaPhoto}
              alt="MUDr. Klaudia Ondrejčíková"
              style={{
                width: "280px",
                height: "360px",
                objectFit: "cover",
                borderRadius: "16px",
                border: "2px solid rgba(255,255,255,0.25)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              }}
            />
            <div
              style={{ marginTop: "14px", fontSize: "18px", fontWeight: 600 }}
            >
              MUDr. Klaudia Ondrejčíková
            </div>
          </div>
        </div>
      </div>

      {/* 🍓 PREČO SI VYBRAŤ NÁS – jahodkový dizajn */}
      <div
        id="preco"
        style={{
          background:
            "linear-gradient(180deg, rgb(250, 226, 226) 0%, #ffffff 100%)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "50px 20px",
          }}
        >
          <h1 style={{ fontSize: "46px", marginBottom: "12px" }}>
            Prečo si vybrať nás?
          </h1>

          <p
            style={{
              maxWidth: "960px",
              opacity: 0.8,
              lineHeight: "28px",
              marginBottom: "36px",
            }}
          >
            Kombinujeme v pediatrii odbornú medicínu, moderné vyšetrenia a
            ľudský prístup v prostredí, kde sa deti aj rodičia cítia bezpečne
            🍓
          </p>

          {/* GRID 2x2 */}
          <div
            className="preco-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "28px",
            }}
          >
            {/* 1 */}
            <div
              style={{
                padding: "28px",
                borderRadius: "22px",
                background: "rgba(248,193,186,0.55)",
                border: "1px solid rgba(72,56,76,0.15)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
              }}
              className="jahoda-box"
            >
              <div style={{ fontSize: "34px", marginBottom: "12px" }}>🍓</div>
              <h3 style={{ marginTop: 0 }}>Pohodlie pre rodičov</h3>
              <ul style={{ paddingLeft: "18px", lineHeight: "28px" }}>
                <li>online rezervácia termínu zadarmo</li>
                <li>presný čas vyšetrenia – bez čakania</li>
                <li>lokalita v širšom centre mesta</li>
              </ul>
            </div>

            {/* 2 */}
            <div
              style={{
                padding: "28px",
                borderRadius: "22px",
                background: "rgba(72,56,76,0.05)",
                border: "1px solid rgba(72,56,76,0.15)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
              }}
              className="jahoda-box"
            >
              <div style={{ fontSize: "34px", marginBottom: "12px" }}>🩺</div>
              <h3 style={{ marginTop: 0 }}>Odbornosť a dôvera</h3>
              <ul style={{ paddingLeft: "18px", lineHeight: "28px" }}>
                <li>pravidelné vzdelávanie v pediatrii</li>
                <li>postupy podľa EBM</li>
                <li>komplexná starostlivosť do 25 rokov</li>
              </ul>
            </div>

            {/* 3 */}
            <div
              style={{
                padding: "28px",
                borderRadius: "22px",
                background: "rgba(248,193,186,0.55)",
                border: "1px solid rgba(72,56,76,0.15)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
              }}
              className="jahoda-box"
            >
              <div style={{ fontSize: "34px", marginBottom: "12px" }}>⚡</div>
              <h3 style={{ marginTop: 0 }}>Rýchle vyšetrenia na mieste</h3>
              <ul style={{ paddingLeft: "18px", lineHeight: "28px" }}>
                <li>
                  otoskopia (vyšetrenie uší) u spolupracujúcich detí priamo u
                  nás
                </li>
                <li>
                  vyšetrenie CRP a hemoglobínu (chudokrvnosť) z 1 kvapky krvi
                </li>
                <li>StrepA test – okamžitý výsledok</li>
                <li>10-parametrové vyšetrenie moču</li>
              </ul>
            </div>

            {/* 4 */}
            <div
              style={{
                padding: "28px",
                borderRadius: "22px",
                background: "rgba(72,56,76,0.05)",
                border: "1px solid rgba(72,56,76,0.15)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
              }}
              className="jahoda-box"
            >
              <div style={{ fontSize: "34px", marginBottom: "12px" }}>🌿</div>
              <h3 style={{ marginTop: 0 }}>Starostlivosť navyše</h3>
              <ul style={{ paddingLeft: "18px", lineHeight: "28px" }}>
                <li>znecitlivenie miesta vpichu Emla krémom</li>
                <li>testy najčastejších respiračných ochorení</li>
                <li>nastreľovanie náušníc prístrojom Blomdahl</li>
                <li>balíčky pre deti a rodičov od Happy Baby</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ KONTAKT – tmavá (striedanie) */}
      <div
        id="kontakt"
        style={{
          backgroundColor: "rgb(72, 56, 76)",
          color: "white",
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "80px 20px",
          }}
        >
          <h1 style={{ fontSize: "48px", color: "white", marginBottom: 10 }}>
            Kontakt
          </h1>
          <p
            style={{
              opacity: 0.65,
              fontSize: "17px",
              maxWidth: 560,
              marginBottom: 48,
            }}
          >
            Sme tu pre vás a vaše deti. Napíšte, zavolajte alebo nás nájdite
            priamo v ambulancii.
          </p>

          <div
            className="kontakt-columns"
            style={{
              display: "flex",
              gap: "24px",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            {/* Ľavá, širšia karta – kontaktné údaje */}
            <div
              className="kontakt-card kontakt-info-grid"
              style={{ flex: "1.6 1 480px" }}
            >
              {contactItems.map((item) => (
                <div className="kontakt-info-cell" key={item.label}>
                  <IconWrap>{item.icon}</IconWrap>
                  <div>
                    <div className="kontakt-label">{item.label}</div>
                    <div className="kontakt-value">{item.content}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pravá, užšia karta – ordinačné hodiny */}
            <div className="kontakt-card" style={{ flex: "1 1 260px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "22px 24px",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <IconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 3" />
                  </svg>
                </IconWrap>
                <div style={{ fontSize: "17px", fontWeight: 600 }}>
                  Ordinačné hodiny
                </div>
              </div>

              <div>
                {openingHours.map((row, i) => {
                  const isToday = row.day === todayName;
                  return (
                    <div
                      key={row.day}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "13px 24px",
                        borderBottom:
                          i === openingHours.length - 1
                            ? "none"
                            : "1px solid rgba(255,255,255,0.08)",
                        background: isToday
                          ? "rgba(248,193,186,0.12)"
                          : "transparent",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: isToday
                            ? "rgb(248,193,186)"
                            : "rgba(255,255,255,0.92)",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {row.day}
                        {isToday && (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              background: "rgb(248,193,186)",
                              color: "rgb(72,56,76)",
                              borderRadius: "999px",
                              padding: "2px 8px",
                            }}
                          >
                            Dnes
                          </span>
                        )}
                      </span>
                      <span style={{ opacity: 0.85 }}>{row.time}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mapa */}
          <div
            style={{
              marginTop: "24px",
              width: "100%",
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3091.1223051024044!2d21.245724499999998!3d48.7263302!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x473ee05dc407d889%3A0x51faa9d358965f76!2sOC%20TipTop%20Ko%C5%A1ice%2C%20%C4%8Ceskoslovenskej%20arm%C3%A1dy%201175%2F18%2C%20040%2001%20Star%C3%A9%20Mesto!5e1!3m2!1ssk!2ssk!4v1752776231073!5m2!1ssk!2ssk"
              width="100%"
              height="350"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              title="Mapa"
            ></iframe>
          </div>

          <div
            style={{
              marginTop: "32px",
              paddingTop: "24px",
              borderTop: "1px solid rgba(255,255,255,0.08)",
              opacity: 0.55,
              fontSize: "14px",
            }}
          >
            © 2026 STAJAMED s. r. o. Všetky práva vyhradené.
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
